import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "../auth/useSession";

/**
 * Screens that need a token — reported as a bug on 2026-08-31.
 *
 * Before this, a signed-out visitor reaching `/tabs` got as far as the library,
 * which asked for a token, got none, and rendered
 * `LIBRARY_LOAD_FAILED · UNAUTHENTICATEDERROR`. That is a diagnosis, not an
 * answer: the person cannot act on it, and the thing they need — a way to sign
 * in — is on a screen they were not sent to.
 *
 * So the check moved in front of the request. `UnauthenticatedError` still
 * exists and is still thrown; it is now the sign of a genuine race (a token
 * expiring mid-session) rather than the ordinary case of not being signed in.
 *
 * Not a boundary. The server refuses these routes regardless (D-26); this only
 * decides what to render.
 */
export function RequireSession({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useSession();
  const location = useLocation();

  // `App` already waits for `GET /auth/session` before rendering routes, so
  // this should not fire - but deciding "signed out" while the session is
  // still resolving is exactly the bug that bounced the owner to /tabs on
  // 2026-08-30, and it is cheap to refuse to make that mistake twice.
  if (isLoading) return null;

  if (!isAuthenticated) {
    /*
     * The requested path rides along so the door can come back to it. Nothing
     * consumes it yet - that is `perks-session-routing` FR-2 - but throwing the
     * destination away here is what makes deep links unrecoverable later.
     */
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
